// Purpose-built product film — a 16s looping sequence that tells the
// "one thread" story: a request becomes a job, a job becomes an invoice, and
// the numbers roll into analytics. Rendered on the /preview-film route at
// 1280×720 and recorded to WebM (see scripts/landing-film.mjs); the resulting
// clip is embedded as an ambient, muted, looping <video> on the landing page.
import React from 'react'
import { Mail, ArrowRight, Calendar, MapPin, FileText, TrendingUp, Check, BarChart3 } from 'lucide-react'
import { LogoMark } from './primitives'
import './landing.css'

const scene: React.CSSProperties = { display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }
const glass: React.CSSProperties = { background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 16, boxShadow: 'var(--shadow-popover-mid)' }

export function FilmScene() {
  return (
    <div data-theme="dark" className="filmstage">
      <div className="filmstage__aura" />
      <div className="filmstage__grid" />
      <div className="filmstage__brand"><LogoMark size={34} /><span>Orkestra</span></div>
      <div className="filmstage__cap">One thread, start to finish</div>

      <div className="filmstage__stagewrap">
        {/* Phase 1 — Request */}
        <div className="film-scene film-scene--1" style={scene}>
          <div className="film-title">A request comes in <b>— from anywhere</b></div>
          <div style={{ ...glass, padding: 22, maxWidth: 560, display: 'flex', gap: 16, alignItems: 'center', borderLeft: '4px solid var(--danger)' }}>
            <span style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--accent-dim)', color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Mail size={22} /></span>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)' }}>Harborview Dental</span>
                <span style={{ fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 999, color: 'var(--danger)', background: 'var(--danger-dim)' }}>URGENT</span>
              </div>
              <div style={{ fontSize: 15, color: 'var(--ink-secondary)', marginTop: 3 }}>“No cooling in Suite 200 — patients arrive at 9.”</div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-tertiary)', marginTop: 6 }}>Email · shared inbox · 7 min ago</div>
            </div>
          </div>
        </div>

        {/* Phase 2 — Dispatch */}
        <div className="film-scene film-scene--2" style={scene}>
          <div className="film-title">Dispatched to the right tech <b>in seconds</b></div>
          <div style={{ ...glass, padding: 20, maxWidth: 600, position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, color: 'var(--ink-secondary)' }}>
              <Calendar size={17} style={{ color: 'var(--accent-text)' }} /><span style={{ fontWeight: 700, fontSize: 14 }}>Today · Dispatch board</span>
            </div>
            {[
              { t: '8:00', c: 'Northgate Apartments', s: 'On site', tone: 'var(--status-job-on-site)', who: 'DR' },
              { t: '8:30', c: 'Harborview Dental', s: 'En route', tone: 'var(--status-job-en-route)', who: 'PP', hi: true },
              { t: '9:15', c: 'Lakeside Grill', s: 'Needs approval', tone: 'var(--status-job-awaiting-approval)', who: 'MB' },
            ].map(r => (
              <div key={r.t} style={{ display: 'grid', gridTemplateColumns: '48px 1fr auto', gap: 14, alignItems: 'center', padding: '11px 12px', marginBottom: 8, borderRadius: 11, background: r.hi ? 'var(--accent-dim)' : 'var(--surface)', border: `1px solid ${r.hi ? 'var(--accent-border)' : 'var(--border-subtle)'}`, borderLeft: `3px solid ${r.tone}` }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-secondary)', fontFamily: "'JetBrains Mono', monospace" }}>{r.t}</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-primary)' }}>{r.c}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: r.tone }}>{r.s}</span>
                  <span style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--avatar-gradient)', color: 'var(--accent-ink)', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{r.who}</span>
                </span>
              </div>
            ))}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 13, fontWeight: 600, color: 'var(--positive)' }}>
              <MapPin size={15} /> Priya assigned & notified — en route
            </div>
          </div>
        </div>

        {/* Phase 3 — Invoice */}
        <div className="film-scene film-scene--3" style={scene}>
          <div className="film-title">Work done, invoiced <b>— with the margin</b></div>
          <div style={{ ...glass, padding: 24, maxWidth: 520 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontSize: 19, fontWeight: 800, color: 'var(--ink-primary)' }}><FileText size={20} style={{ color: 'var(--accent-text)' }} /> INV-1042</span>
              <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 11px', borderRadius: 999, color: 'var(--info)', background: 'var(--info-dim)' }}>Sent · Net 15</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, color: 'var(--ink-secondary)', padding: '8px 0', borderTop: '1px solid var(--border-subtle)' }}><span>Lakeside Grill · compressor swap</span><span style={{ color: 'var(--ink-primary)', fontWeight: 700 }}>$2,336</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', padding: '12px 0 0', marginTop: 8, borderTop: '1px solid var(--border-default)' }}><span>Total due</span><span>$2,523</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, padding: '12px 14px', borderRadius: 11, background: 'var(--positive-dim)', border: '1px solid var(--positive-border)' }}>
              <TrendingUp size={18} style={{ color: 'var(--positive)' }} />
              <span style={{ fontSize: 14, color: 'var(--ink-secondary)' }}>Margin captured from the field: <strong style={{ color: 'var(--positive)' }}>41%</strong></span>
            </div>
          </div>
        </div>

        {/* Phase 4 — Analytics */}
        <div className="film-scene film-scene--4" style={scene}>
          <div className="film-title">And the numbers <b>update themselves</b></div>
          <div style={{ ...glass, padding: 24, maxWidth: 600 }}>
            <div style={{ display: 'flex', gap: 28, alignItems: 'flex-end', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-tertiary)' }}>Revenue · 30 days</div>
                <div className="film-count" style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-0.02em', color: 'var(--ink-primary)' }}>$228,400</div>
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 15, fontWeight: 800, color: 'var(--positive)', paddingBottom: 8 }}><TrendingUp size={17} /> +18%</div>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', height: 120 }}>
              {[34, 41, 39, 52, 49, 63, 61, 78].map((h, i) => (
                <span key={i} className="film-bar" style={{ flex: 1, height: `${h}%`, borderRadius: 6, background: 'linear-gradient(180deg, var(--accent), var(--positive))', animationDelay: `${i * 90}ms` }} />
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, fontSize: 13, color: 'var(--ink-tertiary)' }}>
              <BarChart3 size={15} style={{ color: 'var(--accent-text)' }} /> Live — no exports, no spreadsheets, no month-end scramble.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function FilmRoute() {
  // Bare full-bleed film for capture. Centered so 1280×720 records cleanly.
  return (
    <div className="ork" data-theme="dark" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--canvas)' }}>
      <FilmScene />
    </div>
  )
}
